import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, inputCls, inputStyle } from "@/pages/Login";
import { toast } from "sonner";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null);
  const { loginWith } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const teamInviteToken = params.get("team_invite") || "";

  useEffect(() => {
    const c = params.get("code");
    if (c) setInviteCode(c.toUpperCase());
    if (!teamInviteToken) return;
    api.get(`/team-invites/preview?token=${teamInviteToken}`)
      .then((res) => {
        setInvitePreview(res.data);
        if (res.data?.email) setEmail(res.data.email);
      })
      .catch(() => setInvitePreview({ status: "expired" }));
  }, [params, teamInviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, invite_code: inviteCode, team_invite_token: teamInviteToken });
      loginWith(data.token, data.user);
      if (data.accepted_team_invite?.accepted) {
        toast.success("Team invite accepted.", { description: data.accepted_team_invite.rowan_message || `You now have access to ${data.accepted_team_invite.business_name}.` });
      } else if (data.team_invite_error) {
        toast.error(data.team_invite_error);
      }
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Claim your spot" subtitle="Start your 14-day free trial — no card required. Founding members lock in 40% off for life with code FOUNDING40.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {teamInviteToken && (
          <div data-testid="register-team-invite-banner" className="text-sm px-4 py-3 rounded-2xl" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)", border: "1px solid rgba(232,168,56,0.28)" }}>
            {invitePreview?.status === "expired"
              ? "That team invite looks expired or unavailable. You can still create an account, but we may need a fresh invite after that."
              : `You're joining ${invitePreview?.business_name || "an IronRoot business"} as ${invitePreview?.role_label || "a team member"}. Use ${invitePreview?.email || "the invited email"} so the invite attaches cleanly.`}
          </div>
        )}
        {error && (
          <div data-testid="register-error" className="text-sm px-4 py-3 rounded-xl" style={{ background: "#FDECE8", color: "#B23B1F" }}>
            {error}
          </div>
        )}
        <div data-testid="founding-note" className="text-sm px-4 py-3 rounded-2xl flex items-start gap-2" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)", border: "1px solid rgba(232,168,56,0.3)" }}>
          <span className="font-semibold">🎉 Founding member offer:</span>
          <span>40% off for life with code <span className="font-mono font-semibold">FOUNDING40</span> — applied automatically at checkout after your free trial.</span>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Name</label>
          <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="Jane Bookkeeper" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Email</label>
          <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="you@company.com" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Password</label>
          <input data-testid="register-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="At least 8 characters" />
        </div>
        <button data-testid="register-submit" type="submit" disabled={loading} className="bk-btn-primary w-full py-3 text-sm font-semibold disabled:opacity-60">
          {loading ? "Creating…" : "Claim your spot"}
        </button>
      </form>
      <p className="text-sm mt-6" style={{ color: "var(--bk-text-2)" }}>
        Already have an account?{" "}
        <Link to={teamInviteToken ? `/login?team_invite=${teamInviteToken}` : "/login"} className="font-semibold" style={{ color: "var(--bk-accent)" }} data-testid="link-login">Log in</Link>
      </p>
    </AuthShell>
  );
}
